"use client"

import { Ban, Smartphone, Tag, Wifi, type LucideIcon } from "lucide-react"

import { accountText, type LocaleProps } from "../../personal-web"

type Tip = {
  id: string
  icon: LucideIcon
  titleKey: string
  bodyKey: string
  /**
   * Per-tip Tailwind class set for the icon badge. Each tip gets its own
   * hue so the row reads as four distinct hints instead of a wall of one
   * colour, but the card body stays neutral so the section doesn't shout
   * over the orange Hero / install steps.
   */
  iconBg: string
  iconFg: string
}

const TIPS: Tip[] = [
  {
    id: "wifi",
    icon: Wifi,
    titleKey: "install.tip.wifi.title",
    bodyKey: "install.tip.wifi.body",
    iconBg: "bg-sky-50",
    iconFg: "text-sky-600",
  },
  {
    id: "interrupt",
    icon: Ban,
    titleKey: "install.tip.interrupt.title",
    bodyKey: "install.tip.interrupt.body",
    iconBg: "bg-rose-50",
    iconFg: "text-rose-600",
  },
  {
    id: "label",
    icon: Tag,
    titleKey: "install.tip.label.title",
    bodyKey: "install.tip.label.body",
    iconBg: "bg-violet-50",
    iconFg: "text-violet-600",
  },
  {
    id: "default",
    icon: Smartphone,
    titleKey: "install.tip.default.title",
    bodyKey: "install.tip.default.body",
    iconBg: "bg-emerald-50",
    iconFg: "text-emerald-600",
  },
]

/**
 * "Before you install" hints. Placed between Device Tab and Install section.
 * White card + neutral border keeps the row visually quiet; each tip's icon
 * carries its own hue so the four hints are still distinguishable at a glance.
 * Mobile: 2×2 grid. Desktop: 1×4 strip.
 */
export function ImportantTips({ lng }: LocaleProps) {
  return (
    <section
      aria-label={accountText(lng, "install.tip.ariaLabel")}
      className="grid grid-cols-2 gap-2 md:grid-cols-4"
    >
      {TIPS.map((tip) => {
        const Icon = tip.icon
        return (
          <div
            key={tip.id}
            className="flex flex-col gap-1.5 rounded-xl border border-stone-200 bg-white p-3 transition-colors hover:border-stone-300"
          >
            <span
              aria-hidden
              className={`flex h-8 w-8 items-center justify-center rounded-lg ${tip.iconBg} ${tip.iconFg}`}
            >
              <Icon className="h-4 w-4" />
            </span>
            <h3 className="text-[13px] font-bold leading-tight text-slate-950">
              {accountText(lng, tip.titleKey)}
            </h3>
            <p className="text-[11px] leading-snug text-stone-500">
              {accountText(lng, tip.bodyKey)}
            </p>
          </div>
        )
      })}
    </section>
  )
}
